import debounce from 'lodash.debounce';
import DatePicker from 'vuejs-datepicker';
import Block from './../../common/Block';
import CalendarUtils from './../../helpers/CalendarUtils';
import Configuration from './../../mixins/Configuration';
import Day from './../../common/Day';
import ErrorUtils from './../../helpers/ErrorUtils.js';
import UserTypes from './../../mixins/UserTypes';
import FormHelpers from './../../mixins/FormHelpers';
import Hour from './../../common/Hour';
import PopupZindex from './../../mixins/PopupZindex';
import ServicesHelpers from './../../mixins/ServicesHelpers';
import ReservationFormHelpers from './../../mixins/ReservationFormHelpers';
import ReservationFormParams from './../../ui/ReservationFormParams';
import ReservationFormUtils from './../../helpers/ReservationFormUtils';
import StickyHeader from './../../common/StickyHeader';
import Vue from 'vue';

export default {
  name: 'search-date-form',
  mixins: [
    Configuration,
    FormHelpers,
    ServicesHelpers,
    PopupZindex,
    ReservationFormHelpers,
    UserTypes
  ],
  components: {
    'block': Block,
    'date-picker': DatePicker,
    'day': Day,
    'hour': Hour,
    'reservation-form-params': ReservationFormParams,
    'sticky-header': StickyHeader
  },
  data () {
    return {
      // Popup state
      isVisible: false,
      isLoading: false,
      // Load state
      configurationLoaded: false,
      parametersLoaded: false,
      servicesLoaded: false,
      workersLoaded: false,
      // Validation
      errors: [],
      // Status
      newStatus: false,
      // Form data
      peopleNumber: 1,
      children: 0,
      days: 1,
      duration: {
        hours: 0,
        minutes: 0
      },
      defaultDuration: {
        hours: 0,
        minutes: 0,
        custom_duration_id: 0
      },
      selectedService: '',
      selectedWorker: '',
      periodicity: 0,
      periodicityMode: 0,
      periodicityType: '',
      periodicityConfig: {
        type: 0,
        repeat: 1
      },
      periodicityPaymentType: 0,
      additionalParamsValues: {},
      specialParamsValues: {},
      selectedDate: {
        type: '',
        date: '',
        hour: '',
        worker: ''
      },
      // Date fields
      serviceSelectedDate: '',
      nearestDates: [],
      nearestDatesMessage: '',
      periodicityDates: [],
      periodicityDatesMessage: '',
      // Fields necessary options
      reservationParameters: [],
      servicesCategories: [],
      services: [],
      workers: [],
      serviceAvailableDates: [],
      // Internal helpers
      timePopupStartDate: +new Date(),
      nearestDatesStartDate: +new Date(),
      paramsKey: 1,
      dateSearchMode: 'nearest',
      serviceDatesAreLoading: false,
      nearestDatesAreLoading: false,
      skipDatesReload: false,
      datePickerDate: null,
      timePopupVisible: false,
      timePopupSelectedHour: '00',
      timePopupSelectedMinutes: '00',
      allServices: []
    };
  },
  computed: {
    useDaysMode () {
      if (!this.selectedService) {
        return false;
      }

      let foundedService = this.services.filter(service => service.id === this.selectedService);

      if (foundedService[0]) {
        return foundedService[0].available_in_dates === 3;
      }

      return false;
    },
    selectedServiceIsDailyEndAvailable () {
      if (!this.selectedService) {
        return false;
      }

      let foundedService = this.services.filter(service => service.id === this.selectedService);

      if (foundedService[0]) {
        return !!foundedService[0].daily_end_available;
      }

      return false;
    },
    filteredWorkers () {
      if (this.userIsWorker && this.userCanManageOwn) {
        return this.workers.filter(worker => worker.id === this.$store.state.config.user.id);
      }

      if (this.servicesEnabled && this.selectedService !== '') {
        let workers = this.workers.filter(item => {
          return item.worker.services.filter(service => service.id === this.selectedService).length;
        });

        if (this.selectedWorker !== '') {
          workers = workers.filter(worker => worker.id === this.selectedWorker);
        }

        if (this.userIsWorker && this.userCanManageOwn) {
          return workers.filter(worker => worker.id === this.$store.state.config.user.id);
        }

        return workers;
      }

      if (this.selectedWorker !== '') {
        return this.workers.filter(worker => worker.id === this.selectedWorker)
      }

      return this.workers;
    },
    workersFilteredByService () {
      if (this.servicesEnabled && this.selectedService !== '') {
        let workers = this.workers.filter(item => {
          return item.worker.services.filter(service => service.id === this.selectedService).length;
        });

        return workers;
      }

      return this.workers;
    },
    allDataLoaded () {
      let loaded = true;

      if (!this.configurationLoaded) {
        loaded = false;
      }

      if (this.servicesEnabled && !this.servicesLoaded) {
        loaded = false;
      }

      if (this.workersEnabled && !this.workersLoaded) {
        loaded = false;
      }

      if (this.parametersEnabled && !this.parametersLoaded) {
        loaded = false;
      }

      if (loaded) {
        this.isLoading = false;

        // Set the only available service as default
        if (this.servicesEnabled && this.services.length === 1 && !this.isEditForm) {
          this.selectedService = this.services[0].id;
          this.changeService();
        }
      }

      return loaded;
    },
    dateSwitcherDayName () {
      let date = this.selectedDate.date.split('-');
      let dateObject = new Date(date[0], date[1] - 1, date[2], 0, 0, 0, 0);

      return CalendarUtils.getDayName(dateObject.getDay());
    },
    dateSwitcherFullDate () {
      return this.humanDateFormat(this.selectedDate.date, false);
    },
    dateSwitcherFullMonthDate () {
      return this.humanDateFormatMonth(this.selectedDate.date);
    },
    generatedHours: () => ReservationFormUtils.generatedHours(),
    generatedMinutes: () => ReservationFormUtils.generatedMinutes(),
    generatedPrefixedHours: () => ReservationFormUtils.generatedHours(true, 23),
    generatedPrefixedMinutes: () => ReservationFormUtils.generatedMinutes(true),
    additionalParamsExists () {
      if (this.servicesEnabled && this.selectedService) {
        return !!this.reservationParameters.filter(param => {
          let servicesIDs = param.services.map(service => service.id);

          return param.update_hours === 0 && param.published && (!param.services.length || servicesIDs.indexOf(this.selectedService) > -1);
        }).length;
      }

      if (this.servicesEnabled && this.selectedService === '') {
        return !!this.reservationParameters.filter(param => param.update_hours === 0 && param.published && !param.services.length).length;
      }

      return !!this.reservationParameters.filter(param => param.update_hours === 0 && param.published).length;
    },
    selectedServiceRequiresChildrenNumber () {
      if (!this.useDaysMode) {
        return false;
      }

      let foundedService = this.services.filter(service => service.id === this.selectedService);

      if (foundedService[0]) {
        return foundedService[0].children_required;
      }

      return false;
    },
    selectedServiceRequiresPayment () {
      if (this.servicesEnabled && this.selectedService !== '') {
        let selectedService = this.allServices.filter(service => service.id === this.selectedService);

        if (selectedService[0] && selectedService[0].payment) {
          return true;
        }
      }

      return false;
    }
  },
  watch: {
    'duration.hours': function (newValue) {
      if (this.duration.minutes + newValue > 0) {
        this.clearError('duration');
      }

      this.loadDebouncedData('nearest,dates');
    },
    'duration.minutes': function (newValue) {
      if (this.duration.hours + newValue > 0) {
        this.clearError('duration');
      }

      this.loadDebouncedData('nearest,dates');
    },
    'specialParamsValues': {
      handler: function (newValue) {
        this.loadDebouncedData('nearest,dates');
      },
      deep: true
    },
    'peopleNumber': function (newValue) {
      this.loadDebouncedData('nearest,dates');
      this.clearError('people-number');
    },
    'children': function (newValue) {
      this.loadDebouncedData('nearest,dates');
      this.clearError('children-number');
    },
    'selectedService': function (newValue) {
      this.loadDebouncedData('nearest,dates');
      this.setDefaultDuration(newValue);

      if (this.workersEnabled && this.checkIfServiceIsDaily(newValue)) {
        this.selectedWorker = '';
      }

      if (newValue !== '') {
        this.clearError('service');
      }
    },
    'selectedWorker': function (newValue) {
      if (this.selectedDate.worker !== '') {
        this.selectedDate.worker = newValue;
      }
      this.loadDebouncedData('nearest,dates');
    },
    'periodicityMode': function (newValue) {
      if (newValue === 0) {
        this.periodicity = 0;
        if (this.periodicityConfig.repeat === -1) {
          this.periodicityConfig.repeat = 1;
        }
      } else if (newValue === 1) {
        this.periodicity = 1;
        this.periodicityPaymentType = 0;

        if (this.periodicityConfig.repeat === -1) {
          this.periodicityConfig.repeat = 1;
        }
      } else if (newValue === 2) {
        this.periodicity = 1;
        this.periodicityPaymentType = 1;
        this.periodicityConfig.repeat = -1;
      }
    },
    'periodicityType': function (newValue) {
      this.loadDebouncedData('nearest');
    },
    'periodicityConfig.type': function (newValue) {
      this.loadDebouncedData('nearest,dates');
    },
    'periodicityConfig.repeat': function (newValue) {
      this.loadDebouncedData('nearest,dates');
    },
    'periodicityPaymentType': function (newValue) {
      if (newValue === 0 && this.periodicityConfig.repeat === -1) {
        this.periodicityConfig.repeat = 1;
      }
    },
    'selectedDate.date': function (newValue) {
      if (
        newValue !== '' &&
        (
          (!this.useDaysMode && this.selectedDate.hour) ||
          this.useDaysMode
        )
      ) {
        this.clearError('base-datetime');
      }

      if (newValue !== '') {
        this.loadDebouncedData('nearest,dates');
      }
    },
    'selectedDate.hour': function (newValue) {
      if (newValue !== '' && this.selectedDate.date) {
        this.clearError('base-datetime');
      }

      if (newValue !== '') {
        this.loadDebouncedData('nearest,dates');
      }
    },
    'serviceSelectedDate': function (newValue) {
      if (newValue === '') {
        this.selectedDate.date = '';
        this.selectedDate.hour = '';
      } else {
        let dateTime = newValue.split(' ');
        let date = dateTime[0].split('-').map(n => n.length === 1 ? '0' + n : n).join('-');
        this.selectedDate.date = date;
        this.selectedDate.hour = dateTime[1];
      }
    },
    'dateSearchMode': function (newValue) {
      if (this.skipDatesReload) {
        return;
      }

      if (newValue !== '') {
        this.loadDebouncedData('nearest');
      }
    },
    'nearestDatesStartDate': function (newValue) {
      if (this.skipDatesReload) {
        return;
      }

      if (newValue) {
        this.loadDebouncedData('nearest');
      }
    },
    'days': function () {
      this.loadDebouncedData('nearest,dates');
      this.clearError('days-number');
    }
  },
  mounted () {
    this.$bus.$on('body-clicked', this.bodyClicked);
    this.$bus.$on('show-search-date-popup', this.show);
    this.$bus.$on('hide-search-date-popup', this.close);
    this.$bus.$on('search-date-popup-blockade-added', this.refreshNearestDates);

    if (this.$store.state.config.user.id > 0) {
      this.configurationLoaded = true;
    }
  },
  methods: {
    show () {
      this.isLoading = true;
      this.isVisible = true;
      this.loadData();
      this.increasePopupZindex();
      this.configurationLoaded = false;

      setTimeout(() => {
        this.configurationLoaded = true;
      }, 0);
    },
    close () {
      this.isVisible = false;
      this.isLoading = true;
      this.removePopupZindex();
    },
    loadData () {
      if (!this.servicesEnabled && this.$store.state.config.company.time_increaser > -1) {
        this.duration.hours = Math.floor(this.$store.state.config.company.time_increaser / 60);
        this.duration.minutes = this.$store.state.config.company.time_increaser % 60;
      }

      if (this.userIsWorker) {
        this.selectedWorker = this.$store.state.config.user.id;
      }

      if (this.workersEnabled) {
        this.loadWorkers();
      } else {
        if (this.servicesEnabled) {
          this.loadServices();
        }
      }

      if (this.parametersEnabled) {
        this.loadReservationParams(false);
      }

      if (!this.servicesEnabled) {
        this.loadDebouncedData('nearest,dates');
      }
    },
    loadDebouncedData (toLoad) {
      if (toLoad.indexOf('price') > -1) {
        this.loadDebouncedPrice();
      }

      if (toLoad.indexOf('nearest') > -1) {
        this.nearestDatesAreLoading = true;
        this.loadDebouncedServiceNearestDates();
      }

      if (toLoad.indexOf('dates') > -1) {
        this.serviceDatesAreLoading = true;
        this.loadDebouncedAvailableDates();
      }

      if (toLoad.indexOf('periodicity') > -1) {
        this.loadDebouncedServicePeriodicityDates();
      }
    },
    bodyClicked () {
      this.timePopupVisible = false;
    },
    changeService () {
      this.clearError('worker');

      if (this.selectedService === '') {
        this.defaultDuration.hours = 0;
        this.defaultDuration.minutes = 0;
      } else {
        let selectedService = this.services.filter(service => service.id === this.selectedService)[0];

        if (selectedService) {
          let selectedServiceDuration = selectedService.duration;
          // set hours
          if (this.selectedServiceHasAdditionalPrice) {
            this.defaultDuration.hours = Math.floor(selectedServiceDuration / 60);
            this.defaultDuration.minutes = selectedServiceDuration % 60;
            this.duration.hours = this.defaultDuration.hours;
            this.duration.minutes = this.defaultDuration.minutes;
          } else if (this.selectedServiceCustomDurations.length) {
            this.duration.custom_duration_id = this.selectedServiceCustomDurations[0].id;
          }

          if (this.workersEnabled) {
            let selectedWorker = this.filteredWorkers.filter(worker => worker.id === this.selectedWorker);

            if (!selectedWorker.length && this.filteredWorkers.length === 1) {
              this.selectedWorker = this.filteredWorkers[0].id;
            } else if (!selectedWorker || !selectedWorker.length) {
              this.selectedWorker = '';
            }
          }

          if ((this.servicePeriodicities && this.servicePeriodicities.length) || this.periodicityMode > 0) {
            this.periodicity = 1;
          } else {
            this.periodicity = 0;
          }

          if (this.serviceHasDates) {
            this.serviceSelectedDate = '';
          }

          setTimeout(() => {
            this.periodicityType = '';
          }, 0);
        }
      }
    },
    setDateSearchMode (newValue) {
      this.dateSearchMode = newValue;

      if (newValue === 'nearest') {
        this.selectedDate.date = '';
        this.selectedDate.hour = '';
      } else if (newValue === 'date') {
        this.selectedDate.hour = '';

        if (!this.$refs['date-picker'].isOpen) {
          this.$refs['date-picker'].showCalendar();
        } else {
          this.$refs['date-picker'].close();
        }

        this.timePopupVisible = false;
      } else if (newValue === 'hour') {
        this.selectedDate.date = '';
        this.timePopupVisible = !this.timePopupVisible;
        this.$refs['date-picker'].close();
      }
    },
    loadDebouncedAvailableDates: debounce(function () {
      this.loadAvailableDates();
    }, 500),
    loadAvailableDates () {
      if (!this.serviceHasDates) {
        return;
      }

      let config = {};

      if (this.peopleNumber !== '' && this.selectedServiceRequiresPeopleNumber() && this.peopleNumberEnabled) {
        config.people = this.peopleNumber;
      }

      if (this.servicesEnabled && this.selectedService !== '') {
        config.service = this.selectedService;
      }

      if (this.workersEnabled && this.selectedWorker !== '') {
        config.worker = this.selectedWorker;
      }

      if (!this.selectedServiceHasCustomDuration) {
        config.duration = this.duration.hours * 60 + this.duration.minutes;
      } else {
        config.custom_duration_id = this.duration.custom_duration_id;
      }

      this.$http.post('/panel/reservations/get-service-termins', config).then(response => {
        this.serviceAvailableDates = response.data.data;
        this.serviceDatesAreLoading = false;

        if (this.serviceHasDates && this.serviceAvailableDates && this.serviceAvailableDates.length === 1) {
          this.serviceSelectedDate = this.serviceAvailableDates[0].date + ' ' + this.serviceAvailableDates[0].hour;
        }
      }).catch(error => {
        this.serviceDatesAreLoading = false;
        ErrorUtils.handleSubmitError(error, '/panel/reservations/get-service-termins', this.$router, this.$bus, this.loadAvailableDates.bind(this));
      });
    },
    humanDateFormat (date, withHour = true, showDayName = false) {
      if (!date) {
        return '';
      }

      return CalendarUtils.humanDateFormat(date, withHour, showDayName);
    },
    humanDateFormatMonth (date) {
      if (!date) {
        return '';
      }

      return CalendarUtils.humanDateFormatMonth(date);
    },
    loadDebouncedServiceNearestDates: debounce(function () {
      this.loadServiceNearestDates();
    }, 500),
    loadServiceNearestDates () {
      let config = {};

      if (this.serviceHasDates) {
        this.nearestDatesAreLoading = false;
        return;
      }

      if (!this.useDaysMode && this.dateSearchMode === 'hour' && this.selectedDate.hour === '') {
        this.nearestDatesAreLoading = false;
        return;
      }

      if (!this.useDaysMode && this.dateSearchMode === 'date' && this.selectedDate.date === '') {
        this.nearestDatesAreLoading = false;
        return;
      }

      if (this.servicesEnabled && this.serviceIsCyclic && !this.periodicityType) {
        this.nearestDatesAreLoading = false;
        return;
      }

      if (!this.useDaysMode && this.dateSearchMode === 'hour') {
        config.start_date = CalendarUtils.convertTimestampToString(this.timePopupStartDate);
      }

      if (this.dateSearchMode === 'nearest') {
        config.start_date = CalendarUtils.convertTimestampToString(this.nearestDatesStartDate);
      }

      if (this.dateSearchMode !== 'date') {
        config.weeks = 4;
      }

      if (this.peopleNumber !== '' && this.selectedServiceRequiresPeopleNumber() && this.peopleNumberEnabled) {
        config.people = this.peopleNumber;
      }

      if (this.useDaysMode && this.children !== '' && this.selectedServiceRequiresChildrenNumber) {
        config.children = this.children;
      }

      if (this.useDaysMode) {
        config.days = this.days;
      }

      if (this.servicesEnabled && this.selectedService !== '') {
        config.service = this.selectedService;
      }

      if (!this.useDaysMode && this.serviceIsCyclic && !this.serviceHasTickets && !!this.periodicityType) {
        config.periodicity_id = this.periodicityType;
      }

      if (!this.useDaysMode && this.workersEnabled && this.selectedWorker !== '') {
        config.worker = this.selectedWorker;
      }

      if (!this.useDaysMode && this.dateSearchMode === 'hour' && this.selectedDate.hour !== '') {
        config.hour = this.selectedDate.hour;
      }

      if (this.dateSearchMode === 'date' && this.selectedDate.date !== '') {
        config.date = this.selectedDate.date;
      }

      if (!this.useDaysMode) {
        if (!this.selectedServiceHasCustomDuration) {
          config.duration = this.duration.hours * 60 + this.duration.minutes;
        } else {
          config.custom_duration_id = this.duration.custom_duration_id;
        }
      }

      let reservationParamsObject = this.getReservationParamsObject();

      if (reservationParamsObject) {
        config.plugin_comment = reservationParamsObject;
      }

      if (!this.useDaysMode && !this.serviceHasTickets && this.periodicityType === '' && this.periodicity === 1) {
        config.periodicity_type = this.periodicityConfig.type;
        config.periodicity_number = this.periodicityConfig.repeat;

        if (this.periodicityConfig.repeat === -1) {
          config.periodicity_auto_renew = 1;
        }
      }

      let endpoint = '/panel/reservations/get-first-free-hours';

      if (this.useDaysMode) {
        if (this.dateSearchMode === 'nearest') {
          endpoint = '/panel/reservations/get-first-free-days';
        } else if (this.dateSearchMode === 'date') {
          endpoint = '/panel/reservations/get-date-days';
        }
      } else {
        if (this.dateSearchMode === 'hour') {
          endpoint = '/panel/reservations/get-days-for-hour';
        } else if (this.dateSearchMode === 'date') {
          endpoint = '/panel/reservations/get-date-hours';
        }
      }

      this.$http.post(endpoint, config).then(response => {
        this.nearestDatesAreLoading = false;
        this.nearestDatesMessage = response.data.message;

        if (this.useDaysMode && this.dateSearchMode === 'date') {
          let tempData = response.data.data;
          let dates = Object.keys(tempData);
          this.nearestDates = [];

          for (let i = 0; i < dates.length; i++) {
            this.nearestDates.push(tempData[dates[i]]);
          }
        } else {
          this.nearestDates = response.data.data;
        }
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, endpoint, this.$router, this.$bus, this.loadServiceNearestDates.bind(this));
        this.nearestDatesAreLoading = false;
      });
    },
    getReservationParamsObject () {
      if (Object.keys(this.specialParamsValues).length) {
        let paramsObject = {
          data: {
            parameters: []
          }
        };

        let specialParams = [];

        if (this.$refs['special-params-values']) {
          specialParams = this.$refs['special-params-values'].generateAdditionalParamsObject();
        }

        paramsObject.data.parameters = [].concat(specialParams);
        return JSON.stringify(paramsObject);
      }

      return false;
    },
    selectedWorkerName () {
      let worker = this.workers.filter(worker => worker.id === parseInt(this.selectedDate.worker, 10));

      if (worker.length) {
        return worker[0].name;
      }

      return '';
    },
    changeSelectedDate (newValue) {
      let month = newValue.getMonth() + 1.0;
      let day = newValue.getDate();

      if (month < 10) {
        month = '0' + month;
      }

      if (day < 10) {
        day = '0' + day;
      }

      this.selectedDate.date = [newValue.getFullYear(), month, day].join('-');
    },
    changeTimePopupStartDate (newValue) {
      this.timePopupStartDate = newValue;
    },
    changeNearestDatesStartDate (newValue) {
      this.nearestDatesStartDate = newValue;
    },
    setNewTime () {
      let newTime = this.$refs['time-popup-hour'].value + ':' + this.$refs['time-popup-minutes'].value;
      this.selectedDate.hour = newTime;
      this.timePopupVisible = false;
      this.loadDebouncedData('nearest');
    },
    switchDate (type, offset) {
      if (type === 'day') {
        let date = this.selectedDate.date.split('-');
        let dateObject = new Date(date[0], date[1] - 1, date[2], 0, 0, 0, 0);
        dateObject.setDate(dateObject.getDate() + offset);
        this.selectedDate.date = [dateObject.getFullYear(), dateObject.getMonth() + 1, dateObject.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      } else if (type === 'month') {
        let date = this.selectedDate.date.split('-');
        let dateObject = new Date(date[0], date[1] - 1, date[2], 0, 0, 0, 0);
        dateObject.setMonth(dateObject.getMonth() + offset);
        this.selectedDate.date = [dateObject.getFullYear(), dateObject.getMonth() + 1, dateObject.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      } else if (type === 'hour') {
        let hour = this.selectedDate.hour.split(':');

        if (parseInt(hour[0], 10) + offset >= 24) {
          hour[0] = 0;
        } else if (parseInt(hour[0], 10) + offset < 0) {
          hour[0] = 23;
        } else {
          hour[0] = parseInt(hour[0], 10) + offset;
        }

        if (parseInt(hour[0], 10) < 10) {
          hour[0] = '0' + parseInt(hour[0], 10);
        }

        this.selectedDate.hour = hour.join(':');
      }
    },
    editSelectedDate () {
      this.$refs['date-picker'].showCalendar();
    },
    editSelectedTime () {
      let currentTime = this.selectedDate.hour.split(':');
      this.timePopupSelectedHour = currentTime[0];
      this.timePopupSelectedMinutes = currentTime[1];
      this.timePopupVisible = true;
    },
    selectedDateIsInvalid (dateWithHours) {
      if (dateWithHours === '') {
        return false;
      }

      let date = dateWithHours.split(' ');
      let dateItem = this.serviceAvailableDates.filter(dateItem => dateItem.date === date[0] && dateItem.hour === date[1]);

      if (dateItem.length && dateItem[0].valid === 0) {
        return true;
      }

      return false;
    },
    selectedDateInvalidMessage (dateWithHours) {
      if (dateWithHours === '') {
        return '';
      }

      let date = dateWithHours.split(' ');
      let dateItem = this.serviceAvailableDates.filter(dateItem => dateItem.date === date[0] && dateItem.hour === date[1]);

      if (dateItem.length && dateItem[0].valid === 0) {
        return dateItem[0].message;
      }

      return '';
    },
    selectReservationDate (type, date, hour = null, workerSelectID = null) {
      let workerID = '';

      if (!this.useDaysMode && this.workersEnabled && !this.selectedWorker && this.$refs[workerSelectID]) {
        workerID = this.$refs[workerSelectID][0].value;
      } else if (!this.useDaysMode && this.workersEnabled && this.selectedWorker) {
        workerID = this.selectedWorker;
      }

      let reservationConfig = {
        selectedDateType: type
      };

      if (this.servicesEnabled) {
        reservationConfig.service = this.selectedService;
      }

      if (!this.useDaysMode) {
        if (!this.selectedServiceHasCustomDuration) {
          reservationConfig.duration = this.duration.hours * 60 + this.duration.minutes;
        } else {
          reservationConfig.custom_duration_id = this.duration.custom_duration_id;
        }
      }

      if (this.serviceHasDates) {
        reservationConfig.date = ReservationFormUtils.fixDateFormat(this.serviceSelectedDate.split(' ')[0]);

        if (!this.useDaysMode) {
          reservationConfig.hour = this.serviceSelectedDate.split(' ')[1];
        }
      } else {
        reservationConfig.date = ReservationFormUtils.fixDateFormat(date);

        if (!this.useDaysMode) {
          reservationConfig.hour = hour;
        }
      }

      if (this.selectedServiceRequiresPeopleNumber() && this.peopleNumberEnabled) {
        reservationConfig.people = this.peopleNumber;
      }

      if (this.selectedServiceRequiresChildrenNumber && this.peopleNumberEnabled) {
        reservationConfig.children = this.children;
      }

      if (this.useDaysMode) {
        reservationConfig.days = this.days;
      }

      if (!this.useDaysMode && this.workersEnabled) {
        if (this.serviceHasDates || !workerID) {
          reservationConfig.worker = this.selectedWorker;
        } else {
          reservationConfig.worker = workerID;
        }
      }

      if (!this.useDaysMode && !this.serviceHasTickets && (this.periodicity === 1 || this.serviceIsCyclic)) {
        reservationConfig.periodicity = this.periodicity;
      }

      if (!this.useDaysMode && !this.serviceHasTickets && this.serviceIsCyclic) {
        reservationConfig.periodicity_id = this.periodicityType;
      }

      if (!this.useDaysMode && !this.serviceHasTickets && this.periodicity === 1) {
        reservationConfig.periodicity_type = this.periodicityConfig.type;
        reservationConfig.periodicity_number = this.periodicityConfig.repeat;
        reservationConfig.periodicity_payment_type = this.periodicityPaymentType;

        if (this.periodicityConfig.repeat === -1) {
          reservationConfig.periodicity_auto_renew = 1;
        }
      }

      reservationConfig.specialParamsValues = JSON.stringify(this.specialParamsValues);

      this.$router.push({
        name: 'ReservationFormAddFromSearch',
        query: reservationConfig
      });

      this.close();
    },
    blockReservationDate (date, hour = null, workerSelectID = null) {
      let workerID = '';

      if (!this.useDaysMode && this.workersEnabled && !this.selectedWorker && this.$refs[workerSelectID]) {
        workerID = this.$refs[workerSelectID][0].value;
      } else if (!this.useDaysMode && this.workersEnabled && this.selectedWorker) {
        workerID = this.selectedWorker;
      }

      let reservationConfig = {};

      if (this.servicesEnabled) {
        reservationConfig.service_id = this.selectedService;
      }

      if (!this.useDaysMode) {
        if (!this.selectedServiceHasCustomDuration) {
          reservationConfig.duration = this.duration.hours * 60 + this.duration.minutes;
        } else {
          reservationConfig.custom_duration_id = this.duration.custom_duration_id;
        }
      }

      if (this.serviceHasDates) {
        reservationConfig.date = ReservationFormUtils.fixDateFormat(this.serviceSelectedDate.split(' ')[0]);
        if (!this.useDaysMode) {
          reservationConfig.hour = this.serviceSelectedDate.split(' ')[1];
        }
      } else {
        reservationConfig.date = ReservationFormUtils.fixDateFormat(date);

        if (!this.useDaysMode) {
          reservationConfig.hour = hour;
        }
      }

      if (this.selectedServiceRequiresPeopleNumber() && this.peopleNumberEnabled) {
        reservationConfig.people_number = this.peopleNumber;
      }

      if (this.selectedServiceRequiresChildrenNumber && this.peopleNumberEnabled) {
        reservationConfig.children_number = this.children;
      }

      if (this.useDaysMode) {
        reservationConfig.days = this.days;
      }

      if (!this.useDaysMode && this.workersEnabled) {
        if (this.serviceHasDates || !workerID) {
          reservationConfig.worker_id = this.selectedWorker;
        } else {
          reservationConfig.worker_id = workerID;
        }
      }

      if (!this.useDaysMode && !this.serviceHasTickets && (this.periodicity === 1 || this.serviceIsCyclic)) {
        reservationConfig.periodicity = this.periodicity;
      }

      if (!this.useDaysMode && !this.serviceHasTickets && this.serviceIsCyclic) {
        reservationConfig.periodicity_id = this.periodicityType;
      }

      if (!this.useDaysMode && !this.serviceHasTickets && this.periodicity === 1) {
        reservationConfig.periodicity_type = this.periodicityConfig.type;
        reservationConfig.periodicity_number = this.periodicityConfig.repeat;

        if (this.periodicity && !this.serviceIsCyclic && this.selectedServiceRequiresPayment) {
          reservationConfig.periodicity_payment_type = this.periodicityPaymentType;
        }

        if (this.periodicityConfig.repeat === -1) {
          reservationConfig.periodicity_auto_renew = 1;
        }
      }

      this.$bus.$emit('show-block-date-popup', { reservationConfig });
    },
    showInfo (id) {
      this.$bus.$emit('body-clicked');

      setTimeout(() => {
        this.$refs[id][0].show();
      }, 0);
    },
    setDefaultDuration (serviceID) {
      let foundedService = this.services.filter(service => service.id === serviceID);

      if (foundedService[0]) {
        let duration = parseInt(foundedService[0].duration);
        Vue.set(this.duration, 'hours', Math.floor(duration / 60));
        Vue.set(this.duration, 'minutes', duration % 60);
      }
    },
    refreshNearestDates () {
      this.loadDebouncedData('nearest,dates');
    }
  },
  beforeDestroy () {
    this.$bus.$off('body-clicked', this.bodyClicked);
    this.$bus.$off('show-search-date-popup', this.show);
    this.$bus.$off('hide-search-date-popup', this.close);
    this.$bus.$off('search-date-popup-blockade-added', this.refreshNearestDates);
  }
};



// WEBPACK FOOTER //
// ./src/components/ui/SearchDateForm/script.js?vue&type=script&lang=js&